import React, { useState, useEffect } from "react";
import { useTable } from "react-table";
import "./ReactTable.scss";
import CaretLeftIcon from "../../assets/icons/left-arrow-white.svg";
import CaretRightIcon from "../../assets/icons/right-arrow-white.svg";
import { Skeleton } from "antd";

const ReactTable = ({ columns, data, loading, currentPage, setCurrentPage }) => {
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(Math.ceil(data?.length / pageSize));
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    setTotalPages(Math.ceil(data?.length / pageSize));
  }, [data, pageSize]);

  useEffect(() => {
    let timer;
    if (!loading) {
      timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 1000);
    } else {
      setShowSkeleton(true);
    }

    return () => clearTimeout(timer);
  }, [loading]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: Array.isArray(data) ? data : [],
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleEllipsisClick = (direction) => {
    if (direction === "start") {
      setCurrentPage(Math.max(2, currentPage - 2));
    } else if (direction === "end") {
      setCurrentPage(Math.min(totalPages - 1, currentPage + 2));
    }
  };

  const getPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, rows.length);
    return rows.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;

    items.push(
      <button
        key="prev"
        onClick={handlePrev}
        disabled={currentPage === 1}
      >
        <img
          src={CaretLeftIcon}
          alt="left-arrow"
        />
      </button>,
    );

    items.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={currentPage === 1 ? "active" : ""}
      >
        01
      </button>,
    );

    if (currentPage > 3) {
      items.push(
        <button
          key="start-ellipsis"
          onClick={() => handleEllipsisClick("start")}
        >
          ...
        </button>,
      );
    }

    const startPage = Math.max(2, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? "active" : ""}
        >
          {String(i).padStart(2, "0")}
        </button>,
      );
    }

    if (currentPage < totalPages - 2) {
      items.push(
        <button
          key="end-ellipsis"
          onClick={() => handleEllipsisClick("end")}
        >
          ...
        </button>,
      );
    }

    // Always show the last page
    if (totalPages > 1) {
      items.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={currentPage === totalPages ? "active" : ""}
        >
          {String(totalPages).padStart(2, "0")}
        </button>,
      );
    }

    // Show next arrow
    items.push(
      <button
        key="next"
        onClick={handleNext}
        disabled={currentPage === totalPages}
      >
        <img
          src={CaretRightIcon}
          alt="right-arrow"
        />
      </button>,
    );

    return items;
  };

  return (
    <div className="main_table">
      <div className="table_container">
        {showSkeleton ? (
          <Skeleton
            active
            paragraph={{ rows: 10 }}
          />
        ) : (
          <table
            {...getTableProps()}
            className="table"
          >
            <thead className="table_head">
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  className="head_row"
                >
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              {...getTableBodyProps()}
              className="table_tbody"
            >
              {data.length === 0 ? (
                <tr className="no-hover">
                  <td
                    colSpan={columns.length}
                    className="no-data-cell"
                  >
                    No Data Available
                  </td>
                </tr>
              ) : (
                getPageItems().map((row, index) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="tbody_row"
                      key={index}
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="pagination_container">{renderPagination()}</div>}
    </div>
  );
};

export default ReactTable;
